/**
 * API contracts for the `sales-orders` module (M4) — zod schemas + inferred types
 * shared by apps/api, apps/web and (via generated types) apps/android.
 *
 * Covers three surfaces that share one table pair (`sales_orders` /
 * `sales_order_lines`):
 *   1. admin sales orders   — lifecycle `ej_hanterad → pagaende → packat → skickat`
 *   2. restaurant portal    — ordering, cutoff-filtered calendar, my-orders
 *   3. staff orders         — `registered → ready → collected`, no shipment
 * plus the delivery-schedule configuration (exact port, ARCHITECTURE §8.5).
 *
 * OWNERSHIP: the sales-orders module agent owns this file exclusively.
 * Import from '@warehouse/shared'.
 *
 * Convention: request/command payloads are zod schemas (they cross the wire and
 * are validated); response rows are TS interfaces (the server builds them from
 * typed drizzle selects, so re-parsing them would only cost CPU).
 */
import { z } from 'zod';

import { localeSchema } from '../schemas/common.js';

// ---------------------------------------------------------------------------
// enums
// ---------------------------------------------------------------------------

/** Swedish status names are the domain language and stay Swedish in the data. */
export const salesOrderStatusSchema = z.enum([
  'ej_hanterad',
  'pagaende',
  'packat',
  'skickat',
  'cancelled',
]);
export type SalesOrderStatus = z.infer<typeof salesOrderStatusSchema>;

/** The lifecycle order, used for "may I move from A to B" checks. */
export const SALES_ORDER_FLOW: readonly SalesOrderStatus[] = [
  'ej_hanterad',
  'pagaende',
  'packat',
  'skickat',
];

export const salesOrderSourceSchema = z.enum(['portal', 'bc', 'admin', 'staff']);
export type SalesOrderSource = z.infer<typeof salesOrderSourceSchema>;

export const staffOrderStatusSchema = z.enum(['registered', 'ready', 'collected']);
export type StaffOrderStatus = z.infer<typeof staffOrderStatusSchema>;

export const STAFF_ORDER_FLOW: readonly StaffOrderStatus[] = ['registered', 'ready', 'collected'];

export const salesOrderLineStatusSchema = z.enum(['open', 'picked', 'short', 'packed']);
export type SalesOrderLineStatus = z.infer<typeof salesOrderLineStatusSchema>;

/** ISO weekday 1-7 in storage; the admin UI only offers 1-5 (Mon-Fri). */
export const deliveryWeekdaySchema = z.number().int().min(1).max(7);

/** `HH:MM` (the postgres `HH:MM:SS` form is normalized on the way in). */
export const deliveryCutoffTimeSchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/, 'time must be HH:MM');

const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'date must be YYYY-MM-DD');

/**
 * Ordered quantity in the SALES unit — positive, max 3 decimals. Restaurants
 * order in cartons/pieces; the server converts to base units once, at the
 * boundary (ARCHITECTURE §4).
 */
export const orderQtySchema = z
  .union([z.string(), z.number()])
  .transform((v) => (typeof v === 'number' ? String(v) : v.trim()))
  .pipe(z.string().regex(/^\d{1,11}(\.\d{1,3})?$/, 'invalid quantity'))
  .refine((v) => Number(v) > 0, 'quantity must be greater than zero');

/**
 * Optional client-generated command id. Present ⇒ a retried POST (flaky wifi,
 * double click) is a no-op instead of a second order (ARCHITECTURE §4).
 */
const commandIdSchema = z.uuid().optional();

// ---------------------------------------------------------------------------
// sales orders — requests
// ---------------------------------------------------------------------------

export const salesOrderLineInputSchema = z.object({
  articleId: z.uuid(),
  qty: orderQtySchema,
  /** Sales unit. Omitted ⇒ the article's default sales unit. */
  unitId: z.uuid().optional(),
});
export type SalesOrderLineInput = z.infer<typeof salesOrderLineInputSchema>;

export const salesOrderCreateSchema = z.object({
  commandId: commandIdSchema,
  restaurantId: z.uuid(),
  deliveryDate: isoDateSchema,
  note: z.string().max(2000).optional(),
  lines: z.array(salesOrderLineInputSchema).min(1).max(500),
});
export type SalesOrderCreateInput = z.infer<typeof salesOrderCreateSchema>;

export const salesOrderUpdateSchema = z.object({
  commandId: commandIdSchema,
  deliveryDate: isoDateSchema.optional(),
  note: z.string().max(2000).nullish(),
  /** Full replacement of the line set when present. */
  lines: z.array(salesOrderLineInputSchema).min(1).max(500).optional(),
});
export type SalesOrderUpdateInput = z.infer<typeof salesOrderUpdateSchema>;

export const salesOrderStatusChangeSchema = z.object({
  commandId: commandIdSchema,
  status: salesOrderStatusSchema.exclude(['cancelled']),
});
export type SalesOrderStatusChangeInput = z.infer<typeof salesOrderStatusChangeSchema>;

/**
 * Bulk transitions, "per restaurant (+date)" per spec §5.1: either an explicit
 * id list (what the admin list's checkboxes send) or a restaurant+date group.
 */
export const salesOrderBulkStatusSchema = z
  .object({
    commandId: commandIdSchema,
    status: salesOrderStatusSchema.exclude(['cancelled']),
    orderIds: z.array(z.uuid()).min(1).max(500).optional(),
    restaurantId: z.uuid().optional(),
    deliveryDate: isoDateSchema.optional(),
  })
  .refine(
    (v) => (v.orderIds?.length ?? 0) > 0 || (v.restaurantId !== undefined && v.deliveryDate !== undefined),
    'either orderIds or restaurantId + deliveryDate is required',
  );
export type SalesOrderBulkStatusInput = z.infer<typeof salesOrderBulkStatusSchema>;

export const salesOrderCancelSchema = z.object({
  commandId: commandIdSchema,
  reason: z.string().max(500).optional(),
});
export type SalesOrderCancelInput = z.infer<typeof salesOrderCancelSchema>;

// ---------------------------------------------------------------------------
// sales orders — responses
// ---------------------------------------------------------------------------

export interface SalesOrderLineRow {
  id: string;
  articleId: string;
  articleNo: string;
  articleName: string;
  articleNameZh: string | null;
  categoryId: string | null;
  categoryName: string | null;
  qty: string;
  unitId: string;
  unitCode: string;
  qtyBase: string;
  pickedQtyBase: string;
  packedQtyBase: string;
  status: SalesOrderLineStatus;
  shortageReason: string | null;
  sort: number;
}

export interface SalesOrderListRow {
  id: string;
  orderNo: string;
  restaurantId: string;
  restaurantName: string;
  restaurantCode: string;
  status: SalesOrderStatus;
  staffStatus: StaffOrderStatus | null;
  source: SalesOrderSource;
  isStaffOrder: boolean;
  orderDate: string;
  deliveryDate: string;
  note: string | null;
  lineCount: number;
  placedByName: string | null;
  collectedBy: string | null;
  collectedAt: string | null;
  cutoffBypassed: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SalesOrderDetail extends SalesOrderListRow {
  lines: SalesOrderLineRow[];
  /** Populated for admins: the deadline that applied to this delivery date. */
  orderByDueAt: string | null;
}

/** One row of the admin list's "per restaurant + delivery date" grouping. */
export interface SalesOrderGroupRow {
  restaurantId: string;
  restaurantName: string;
  restaurantCode: string;
  deliveryDate: string;
  orderCount: number;
  lineCount: number;
  statuses: Record<SalesOrderStatus, number>;
}

export interface SalesOrderHistoryEntry {
  at: string;
  action: string;
  userName: string | null;
  changes: unknown;
}

// ---------------------------------------------------------------------------
// staff orders (spec §5.14)
// ---------------------------------------------------------------------------

export const staffOrderCreateSchema = z.object({
  commandId: commandIdSchema,
  /** Which unit the employee belongs to; staff orders are collected on site. */
  restaurantId: z.uuid(),
  /** Free-text name of the person who will collect the order. */
  collectedBy: z.string().trim().min(1).max(200).optional(),
  note: z.string().max(2000).optional(),
  lines: z.array(salesOrderLineInputSchema).min(1).max(200),
});
export type StaffOrderCreateInput = z.infer<typeof staffOrderCreateSchema>;

export const staffOrderStatusChangeSchema = z.object({
  commandId: commandIdSchema,
  status: staffOrderStatusSchema,
  /** Required by the server when moving to `collected`. */
  collectedBy: z.string().trim().min(1).max(200).optional(),
});
export type StaffOrderStatusChangeInput = z.infer<typeof staffOrderStatusChangeSchema>;

export interface StaffOrderStats {
  total: number;
  byStatus: Record<StaffOrderStatus, number>;
  /** Most-ordered articles across the window, descending. */
  topArticles: { articleId: string; articleNo: string; articleName: string; qtyBase: string; orders: number }[];
  /** Orders per calendar month, ascending (`YYYY-MM`). */
  byMonth: { month: string; orders: number }[];
}

// ---------------------------------------------------------------------------
// delivery schedule (exact port — ARCHITECTURE §8.5)
// ---------------------------------------------------------------------------

export const deliveryScheduleCreateSchema = z.object({
  commandId: commandIdSchema,
  restaurantId: z.uuid(),
  /** null / omitted = the restaurant-wide default row. */
  categoryId: z.uuid().nullish(),
  deliveryDay1: deliveryWeekdaySchema,
  dueDay1: deliveryWeekdaySchema,
  dueTime1: deliveryCutoffTimeSchema.nullish(),
  deliveryDay2: deliveryWeekdaySchema,
  dueDay2: deliveryWeekdaySchema,
  dueTime2: deliveryCutoffTimeSchema.nullish(),
});
export type DeliveryScheduleCreateInput = z.infer<typeof deliveryScheduleCreateSchema>;

/** `categoryId` is immutable after creation, exactly as in the old system. */
export const deliveryScheduleUpdateSchema = z.object({
  commandId: commandIdSchema,
  deliveryDay1: deliveryWeekdaySchema,
  dueDay1: deliveryWeekdaySchema,
  dueTime1: deliveryCutoffTimeSchema.nullish(),
  deliveryDay2: deliveryWeekdaySchema,
  dueDay2: deliveryWeekdaySchema,
  dueTime2: deliveryCutoffTimeSchema.nullish(),
});
export type DeliveryScheduleUpdateInput = z.infer<typeof deliveryScheduleUpdateSchema>;

export const deliveryScheduleCustomCreateSchema = z.object({
  commandId: commandIdSchema,
  restaurantId: z.uuid(),
  /** The originally scheduled delivery date being moved. */
  changedDate: isoDateSchema,
  /** Its replacement. */
  newDate: isoDateSchema,
  note: z.string().max(500).nullish(),
});
export type DeliveryScheduleCustomCreateInput = z.infer<typeof deliveryScheduleCustomCreateSchema>;

export interface DeliveryScheduleRow {
  id: string;
  restaurantId: string;
  restaurantName: string;
  restaurantCode: string;
  categoryId: string | null;
  categoryName: string | null;
  deliveryDay1: number;
  dueDay1: number;
  dueTime1: string | null;
  deliveryDay2: number | null;
  dueDay2: number | null;
  dueTime2: string | null;
  updatedByName: string | null;
  updatedAt: string;
}

export interface DeliveryScheduleCustomRow {
  id: string;
  restaurantId: string;
  restaurantName: string;
  changedDate: string;
  newDate: string;
  note: string | null;
  updatedByName: string | null;
  updatedAt: string;
}

/** `GET /api/orders/delivery/:restaurantId` — everything the calendar needs. */
export interface RestaurantDeliveryConfig {
  restaurantId: string;
  restaurantName: string;
  schedules: DeliveryScheduleRow[];
  customDates: DeliveryScheduleCustomRow[];
}

/** Restaurants that have no default row — the admin page's warning banner. */
export interface DeliveryScheduleCoverage {
  restaurantId: string;
  restaurantName: string;
  restaurantCode: string;
  hasDefault: boolean;
  categoryCount: number;
}

// ---------------------------------------------------------------------------
// portal ordering (spec §5.13)
// ---------------------------------------------------------------------------

/** A restaurant the signed-in user may order for (`restaurant_users`). */
export interface PortalRestaurant {
  id: string;
  name: string;
  code: string;
  hasSchedule: boolean;
}

/** Assortment row — NO PRICES ANYWHERE (DATA-MODEL preamble). */
export interface PortalAssortmentRow {
  articleId: string;
  articleNo: string;
  name: string;
  nameZh: string | null;
  categoryId: string | null;
  categoryName: string | null;
  priority: number;
  /** The unit the restaurant orders in. */
  unitId: string;
  unitCode: string;
  unitName: string;
  /** How many base units one sales unit is — shown as "1 KRT = 12 ST". */
  qtyInBase: string;
  baseUnitCode: string;
}

export interface PortalDeliveryDate {
  date: string;
  /** ISO instant this date must be ordered by; null = no cutoff configured. */
  dueAt: string | null;
  /** Set when the date replaces an originally scheduled one (holiday move). */
  movedFrom?: string;
}

export interface PortalDeliveryDatesResponse {
  restaurantId: string;
  dates: PortalDeliveryDate[];
  /** Delivery days inside the window whose deadline has already passed. */
  passed: string[];
  /** True when the restaurant has no delivery schedule — the UI blocks submit. */
  scheduleMissing: boolean;
  windowStart: string;
  windowEnd: string;
  /** Echoed so the client can show "showing dates for FRYST + KYLT". */
  categoryIds: string[];
}

export const portalOrderCreateSchema = z.object({
  commandId: commandIdSchema,
  restaurantId: z.uuid(),
  deliveryDate: isoDateSchema,
  note: z.string().max(2000).optional(),
  lines: z.array(salesOrderLineInputSchema).min(1).max(500),
  /** Recipient language for the confirmation mail; defaults to the user's. */
  language: localeSchema.optional(),
});
export type PortalOrderCreateInput = z.infer<typeof portalOrderCreateSchema>;

/**
 * The portal basket kept between visits. Persisted client-side today
 * (localStorage, per user + restaurant); see the module README for the
 * `sales_order_drafts` table request that would move it server-side.
 */
export const portalDraftSchema = z.object({
  version: z.literal(1),
  restaurantId: z.uuid(),
  deliveryDate: isoDateSchema.nullable(),
  note: z.string().max(2000).default(''),
  lines: z
    .array(
      z.object({
        articleId: z.uuid(),
        qty: z.string(),
        unitId: z.uuid().optional(),
      }),
    )
    .default([]),
  updatedAt: z.string(),
});
export type PortalDraft = z.infer<typeof portalDraftSchema>;

// ---------------------------------------------------------------------------
// command types registered with the CommandBus
// ---------------------------------------------------------------------------

/**
 * Every mutation in this module is a registered command, so the same type works
 * from the web AND from `POST /api/sync/commands` (the future Android app packs
 * and dispatches orders offline).
 */
export const SALES_ORDER_COMMANDS = {
  create: 'salesOrder.create',
  update: 'salesOrder.update',
  setStatus: 'salesOrder.setStatus',
  bulkStatus: 'salesOrder.bulkStatus',
  cancel: 'salesOrder.cancel',
  staffCreate: 'staffOrder.create',
  staffSetStatus: 'staffOrder.setStatus',
  scheduleCreate: 'deliverySchedule.create',
  scheduleUpdate: 'deliverySchedule.update',
  scheduleDelete: 'deliverySchedule.delete',
  customDateCreate: 'deliveryDate.override',
  customDateDelete: 'deliveryDate.overrideDelete',
} as const;

export type SalesOrderCommandType = (typeof SALES_ORDER_COMMANDS)[keyof typeof SALES_ORDER_COMMANDS];

/** `salesOrder.bulkStatus` result — how many orders actually moved. */
export interface SalesOrderBulkStatusResult {
  updated: number;
  skipped: number;
  orderIds: string[];
}

/**
 * Delta resource this module publishes to the Android reference cache: the
 * per-restaurant delivery windows the receive/pick screens show as context.
 * `delivery-info` is one of the names `DeltaPuller.Resource.DELIVERY_INFO`
 * matches on.
 */
export const SALES_ORDER_DELTA_RESOURCES = {
  deliveryInfo: 'delivery-info',
} as const;
